import apiClient from "../lib/api-client";

function mutationHeaders() {
  return { headers: { "Idempotency-Key": crypto.randomUUID() } };
}

export interface StockTransfer {
  id: string;
  tenantId: string;
  transferNumber: string;
  transferDate: string;
  sourceStoreId: string;
  sourceStore?: {
    id: string;
    name: string;
  };
  destStoreId: string;
  destStore?: {
    id: string;
    name: string;
  };
  status:
    | "DRAFT"
    | "APPROVED"
    | "IN_TRANSIT"
    | "RECEIVED"
    | "CANCELLED"
    | "REJECTED";
  requestedBy: string;
  approvedBy?: string;
  approvedAt?: string;
  sentAt?: string;
  receivedAt?: string;
  receivedBy?: string;
  notes?: string;
  items?: StockTransferItem[];
  createdAt: string;
  updatedAt: string;
  _count?: {
    items: number;
  };
}

export interface StockTransferItem {
  id: string;
  transferId: string;
  productId: string;
  product?: {
    id: string;
    name: string;
    sku: string;
  };
  batchId: string;
  batch?: {
    id: string;
    batchNumber: string;
    expiryDate: string;
    mrp?: number;
  };
  qtyRequested: number;
  qtySent: number;
  qtyReceived: number;
  qtyRejected: number;
  rejectionReason?: string;
  notes?: string;
}

export interface CreateStockTransferItemDto {
  productId: string;
  batchId: string;
  qtyRequested: number;
  notes?: string;
}

export interface CreateStockTransferDto {
  sourceStoreId: string;
  destStoreId: string;
  items: CreateStockTransferItemDto[];
  notes?: string;
}

export interface ReceiveTransferItemDto {
  itemId: string;
  qtyReceived: number;
  qtyRejected?: number;
  rejectionReason?: string;
}

export interface ReceiveStockTransferDto {
  items: ReceiveTransferItemDto[];
  notes?: string;
}

export interface StockTransferResponse {
  data: StockTransfer[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const stockTransferService = {
  async create(data: CreateStockTransferDto): Promise<StockTransfer> {
    const response = await apiClient.post(
      "/stock-transfers",
      data,
      mutationHeaders(),
    );
    return response.data;
  },

  async approve(transferId: string): Promise<StockTransfer> {
    const response = await apiClient.post(
      `/stock-transfers/${transferId}/approve`,
      undefined,
      mutationHeaders(),
    );
    return response.data;
  },

  async send(transferId: string): Promise<StockTransfer> {
    const response = await apiClient.post(
      `/stock-transfers/${transferId}/send`,
      undefined,
      mutationHeaders(),
    );
    return response.data;
  },

  async receive(
    transferId: string,
    data: ReceiveStockTransferDto,
  ): Promise<StockTransfer> {
    const response = await apiClient.post(
      `/stock-transfers/${transferId}/receive`,
      data,
      mutationHeaders(),
    );
    return response.data;
  },

  async getAll(params?: {
    page?: number;
    limit?: number;
    status?: string;
    sourceStoreId?: string;
    destStoreId?: string;
  }): Promise<StockTransferResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.status) searchParams.append("status", params.status);
    if (params?.sourceStoreId)
      searchParams.append("sourceStoreId", params.sourceStoreId);
    if (params?.destStoreId)
      searchParams.append("destStoreId", params.destStoreId);

    const response = await apiClient.get(`/stock-transfers?${searchParams}`);
    return response.data;
  },

  async getById(id: string): Promise<StockTransfer> {
    const response = await apiClient.get(`/stock-transfers/${id}`);
    return response.data;
  },
};
